import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.jsx';
import { useAnalytics } from '../hooks/useAnalytics.js';
import NotificationBell from './NotificationBell.jsx';
import SsoDeprecationBanner from './SsoDeprecationBanner.jsx';
import TendrilLauncher from './tendril/TendrilLauncher.jsx';
import TendrilChatPanel from './tendril/TendrilChatPanel.jsx';

const AI_HEALTH_VIEW = { label: 'AI Health', path: '/admin/ai-assessment', end: false };

/**
 * Module-based navigation configuration.
 * Each module has a label, sub-views, and optional role requirements.
 * Adding a new module = adding an entry here. The layout handles the rest.
 */
export function getModules(isAdmin, isLeader, isTechLeader, isCeo, isInstructor) {
  const canAccessAdmin = isAdmin || isLeader;
  const leaderOnly = isLeader && !isAdmin;

  const workshopsModule = {
    id: 'workshops',
    label: 'Workshops',
    basePath: '/workshops',
    views: [
      { label: 'Workshop Catalog', path: '/workshops' },
      ...(isAdmin ? [{ label: 'Manage Workshops', path: '/admin/workshops' }] : []),
      ...(isAdmin || isLeader ? [{ label: 'Recordings', path: '/admin/workshop-recordings' }] : []),
    ],
  };

  // FEAT-067: the Teaching area is shown to any assigned-instructor pool member
  // regardless of base role (Approach A — "instructor" is a derived capability,
  // not a login role). Injected into EVERY return branch below because the most
  // common instructor is a plain `user` (who hits the standard-user early return).
  const teachingModules = isInstructor
    ? [
        {
          id: 'teaching',
          label: 'Teaching',
          basePath: '/teaching/sessions',
          views: [{ label: 'My Sessions', path: '/teaching/sessions' }],
        },
      ]
    : [];

  // Tech Stack survey module. CEO / Tech Leader answer for their own company;
  // admin and leader review every company from the report (leaders read-only).
  // /tech-stack is listed in alsoPaths so the tab stays active on a respondent
  // page opened on a company's behalf (?opco=…).
  const techStackModule = {
    id: 'techstack',
    label: 'Tech Stack',
    basePath: canAccessAdmin ? '/admin/tech-survey' : '/tech-stack',
    alsoPaths: ['/tech-stack'],
    views: canAccessAdmin
      ? [{ label: 'Company Tech Stacks', path: '/admin/tech-survey' }]
      : [{ label: 'My Tech Stack', path: '/tech-stack' }],
  };

  // Tech Leader: sees AI Initiatives + Tech Stack + Workshops
  if (isTechLeader && !isAdmin && !isLeader) {
    return [
      {
        id: 'initiatives',
        label: 'AI Initiatives',
        basePath: '/tracker',
        views: [
          { label: 'My Initiatives', path: '/tracker' },
        ],
      },
      techStackModule,
      workshopsModule,
      ...teachingModules,
    ];
  }

  // Standard user (not ceo/admin/leader/tech_leader): only Workshops (+ Teaching)
  if (!isCeo && !isAdmin && !isLeader && !isTechLeader) {
    return [workshopsModule, ...teachingModules];
  }

  return [
    workshopsModule,
    ...teachingModules,
    {
      id: 'assessment',
      label: 'AI Assessment',
      basePath: leaderOnly ? '/admin/ai-assessment' : '/assessment',
      // AI Health spans /admin/ai-assessment/:groupSlug, so its link matches by prefix (end: false).
      views: leaderOnly
        ? [AI_HEALTH_VIEW]
        : [
            { label: 'My Assessment', path: '/assessment' },
            ...(isAdmin ? [AI_HEALTH_VIEW] : []),
          ],
    },
    {
      id: 'initiatives',
      label: 'AI Initiatives',
      basePath: '/tracker',
      views: [
        { label: 'My Initiatives', path: '/tracker' },
        ...(isAdmin || isLeader ? [{ label: 'Portfolio View', path: '/admin/initiatives' }] : []),
      ],
    },
    techStackModule,
    ...(canAccessAdmin
      ? [
          {
            id: 'scorecard',
            label: 'AI Scorecard',
            basePath: '/admin/scorecard',
            views: [
              { label: 'Portfolio Scorecard', path: '/admin/scorecard' },
            ],
          },
          {
            id: 'admin',
            label: 'Admin',
            basePath: '/admin/users',
            views: [
              { label: 'Users', path: '/admin/users' },
              { label: 'Organization', path: '/admin/org' },
              ...(isAdmin ? [{ label: 'Question Sets', path: '/admin/question-sets' }] : []),
              ...(isAdmin ? [{ label: 'Analytics', path: '/admin/analytics' }] : []),
              { label: 'Pipeline Overview', path: '/admin/pipeline' },
              ...(isAdmin ? [{ label: 'Communications', path: '/admin/communications' }] : []),
              ...(isAdmin ? [{ label: 'Service Keys', path: '/admin/service-keys' }] : []),
              ...(isAdmin ? [{ label: 'Feedback', path: '/admin/feedback' }] : []),
              ...(isAdmin ? [{ label: 'BMB Telemetry', path: '/admin/bmb-telemetry' }] : []),
            ],
          },
        ]
      : []),
  ];
}

function getActiveModule(modules, pathname) {
  // Find which module owns the current path.
  // Score by longest matching path to avoid ambiguity (e.g., /admin vs /admin/users)
  let best = null;
  let bestLen = -1;
  for (const mod of modules) {
    const paths = [...mod.views.map((v) => v.path), ...(mod.alsoPaths ?? [])];
    for (const p of paths) {
      if (pathname === p || pathname.startsWith(p + '/')) {
        if (p.length > bestLen) {
          bestLen = p.length;
          best = mod;
        }
      }
    }
  }
  return best ?? null;
}

export default function AppLayout() {
  const { user, profile, logout, isAdmin, isLeader, isCeo, isTechLeader, isInstructor } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const isPending = profile?.status === 'pending_mapping';

  const { trackPageView } = useAnalytics();
  const prevPathRef = useRef(location.pathname);

  // Track page views on route changes
  useEffect(() => {
    const from = prevPathRef.current;
    const to = location.pathname;
    if (from !== to) {
      trackPageView(to, from);
      prevPathRef.current = to;
    }
  }, [location.pathname, trackPageView]);

  const modules = getModules(isAdmin, isLeader, isTechLeader, isCeo, isInstructor);
  const activeModule = getActiveModule(modules, location.pathname);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileExpandedModule, setMobileExpandedModule] = useState(null);
  const [tendrilOpen, setTendrilOpen] = useState(false);

  function handleMobileNav(path) {
    navigate(path);
    setMobileMenuOpen(false);
    setMobileExpandedModule(null);
  }

  // Skip module nav on landing, welcome, and profile pages
  const showModuleNav = !['/', '/welcome', '/profile'].includes(location.pathname);

  return (
    <div
      className={`app-layout${tendrilOpen ? ' tendril-open' : ''}`}
    >
      {/* ── Primary header ── */}
      <header className="app-header">
        <div className="header-inner">
          <Link to="/" className="brand-logo">
            Banyan AI
            {profile?.opcoName && !isAdmin && !isLeader && (
              <span className="brand-logo__opco"> | {profile.opcoName}</span>
            )}
          </Link>

          {/* Desktop module tabs */}
          <nav className="module-nav" aria-label="Module navigation">
            {modules.map((mod) => (
              <button
                key={mod.id}
                className={`module-tab${activeModule?.id === mod.id ? ' module-tab--active' : ''}`}
                onClick={() => navigate(mod.basePath)}
              >
                {mod.label}
              </button>
            ))}
          </nav>

          {/* User section */}
          <div className="header-user">
            {user && (
              <button
                className="user-info user-info--link"
                onClick={() => navigate('/profile')}
                title="View profile"
                aria-label="Go to your profile"
              >
                {user.email}
              </button>
            )}
            <NotificationBell />
            {(isCeo || isTechLeader) && !isAdmin && !isLeader && (
              <button
                className="btn-team"
                onClick={() => navigate('/team')}
                title="Manage your team"
                aria-label="My Team"
              >
                My Team
              </button>
            )}
            <button className="btn-logout" onClick={logout}>
              Sign out
            </button>
          </div>

          {/* Mobile notification bell — visible outside hamburger menu */}
          <div className="mobile-notif-bell">
            <NotificationBell />
          </div>

          {/* Mobile hamburger */}
          <button
            className="hamburger"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
            aria-expanded={mobileMenuOpen}
          >
            <span className={`hamburger__icon${mobileMenuOpen ? ' hamburger__icon--open' : ''}`} />
          </button>
        </div>
      </header>

      {/* ── Sub-navigation for active module (desktop) ── */}
      {showModuleNav && activeModule && activeModule.views.length >= 1 && (
        <nav className="sub-nav" aria-label={`${activeModule.label} views`}>
          <div className="sub-nav__inner">
            {activeModule.views.map((view) => (
              <NavLink
                key={view.path}
                to={view.path}
                end={view.end !== false}
                className={({ isActive }) =>
                  'sub-nav__link' + (isActive ? ' sub-nav__link--active' : '')
                }
              >
                {view.label}
              </NavLink>
            ))}
          </div>
        </nav>
      )}

      {/* ── Mobile menu ── */}
      {mobileMenuOpen && (
        <div className="mobile-menu" role="navigation" aria-label="Mobile navigation">
          <button
            type="button"
            className="mobile-menu__overlay"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Close menu"
          />
          <div className="mobile-menu__panel">
            {/* User info */}
            <div className="mobile-menu__user">
              <span className="mobile-menu__email">{user?.email}</span>
              <button
                className="mobile-menu__item"
                onClick={() => handleMobileNav('/profile')}
              >
                My Profile
              </button>
              {(isCeo || isTechLeader) && !isAdmin && !isLeader && (
                <button
                  className="mobile-menu__item"
                  onClick={() => handleMobileNav('/team')}
                >
                  My Team
                </button>
              )}
            </div>

            <div className="mobile-menu__divider" />

            {/* Modules with expandable sub-views */}
            {modules.map((mod) => (
              <div key={mod.id} className="mobile-menu__module">
                <button
                  className={`mobile-menu__module-header${
                    activeModule?.id === mod.id ? ' mobile-menu__module-header--active' : ''
                  }`}
                  onClick={() =>
                    setMobileExpandedModule(
                      mobileExpandedModule === mod.id ? null : mod.id
                    )
                  }
                >
                  {mod.label}
                  <span className={`mobile-menu__chevron${
                    mobileExpandedModule === mod.id ? ' mobile-menu__chevron--open' : ''
                  }`}>
                    &#x203A;
                  </span>
                </button>
                {mobileExpandedModule === mod.id && (
                  <div className="mobile-menu__sub-views">
                    {mod.views.map((view) => (
                      <button
                        key={view.path}
                        className={`mobile-menu__item${
                          location.pathname === view.path ||
                          (view.end === false && location.pathname.startsWith(view.path + '/'))
                            ? ' mobile-menu__item--active'
                            : ''
                        }`}
                        onClick={() => handleMobileNav(view.path)}
                      >
                        {view.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="mobile-menu__divider" />

            <button className="mobile-menu__item mobile-menu__item--logout" onClick={logout}>
              Sign out
            </button>
          </div>
        </div>
      )}

      {/* sso-warning: in-app banner for password sessions; gated by env + isPasswordSession */}
      <SsoDeprecationBanner />

      {isPending && (
        <div className="pending-banner" role="alert">
          Account not linked to a company
        </div>
      )}
      <main className="app-main">
        <Outlet />
      </main>

      {/* Tendril AI assistant — mounted outside <Outlet> so it persists across routes */}
      <TendrilLauncher onClick={() => setTendrilOpen(true)} />
      <TendrilChatPanel isOpen={tendrilOpen} onClose={() => setTendrilOpen(false)} />
    </div>
  );
}
